import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { formatMoney } from "@/lib/currency";

export function LoadingScreen({ message = "Loading BuildHof…" } = {}) {
  return (
    <main className="page-shell grid min-h-screen place-items-center" data-testid="loading-screen">
      <div className="flex items-center gap-3 rounded-lg border bg-white/80 px-5 py-4 shadow-sm" data-testid="loading-message">
        <Loader2 className="h-5 w-5 animate-spin text-primary" aria-hidden="true" />
        <span className="font-medium">{message}</span>
      </div>
    </main>
  );
}

/** BuildHof door-mark icon used across marketing, auth, dashboard, onboarding. */
export function BuildHofLogoIcon({ light = false, size = 40, className = "" }) {
  const stroke = light ? "#e9e2d2" : "#16332b";
  return (
    <svg
      width={size}
      height={size}
      viewBox="40 70 160 150"
      className={className}
      aria-hidden="true"
      data-testid="brand-logo-mark"
    >
      <path d="M80 185 L80 140 Q80 100 120 100 Q160 100 160 140 L160 185" fill="none" stroke={stroke} strokeWidth="9" strokeLinecap="round" />
      <path d="M109 185 L109 145 Q109 135 120 135 Q131 135 131 145 L131 185 Z" fill="#d97a4a" />
      <circle cx="126" cy="167" r="3" fill={light ? "#e9e2d2" : "#16332b"} />
      <line x1="70" y1="189" x2="170" y2="189" stroke={stroke} strokeWidth="3.5" strokeLinecap="round" />
    </svg>
  );
}

export function LogoMark({ compact = false, light = false, size = 40 }) {
  return (
    <Link to="/" className="flex items-center gap-3 tap-target" data-testid="brand-logo-link" aria-label="BuildHof home">
      <BuildHofLogoIcon light={light} size={size} />
      {!compact && (
        <span
          className={cn("font-sora text-lg font-bold", light ? "text-white" : "text-foreground")}
          data-testid="brand-logo-text"
        >
          BuildHof
        </span>
      )}
    </Link>
  );
}

export function Eyebrow({ children, testId = "section-eyebrow" }) {
  return <Badge data-testid={testId} className="w-fit border-0 bg-[#EAF2E8] px-3 py-1 text-[#173B2F] hover:bg-[#EAF2E8]">{children}</Badge>;
}

export function CTAButton({ to, children, testId, variant = "default", onClick, className }) {
  const content = (
    <Button onClick={onClick} data-testid={testId} className={cn("tap-target rounded-lg px-5 text-sm font-bold shadow-sm transition-[transform,background-color] hover:-translate-y-0.5", className)} variant={variant}>
      {children}
      <ArrowRight className="h-4 w-4" aria-hidden="true" />
    </Button>
  );
  return to ? <Link to={to} data-testid={`${testId}-link`}>{content}</Link> : content;
}

export function StatCard({ label, value, detail, testId }) {
  return (
    <article className="rounded-lg border bg-white/82 p-5 shadow-sm" data-testid={testId}>
      <p className="text-sm text-muted-foreground" data-testid={`${testId}-label`}>{label}</p>
      <p className="mt-2 text-3xl font-extrabold" data-testid={`${testId}-value`}>{value}</p>
      {detail && <p className="mt-1 text-sm text-muted-foreground" data-testid={`${testId}-detail`}>{detail}</p>}
    </article>
  );
}

export function EmptyState({ title, body, action, testId }) {
  return (
    <section className="rounded-lg border border-dashed bg-white/70 p-8 text-center" data-testid={testId}>
      <CheckCircle2 className="mx-auto h-8 w-8 text-accent" aria-hidden="true" />
      <h3 className="mt-4 text-xl font-bold" data-testid={`${testId}-title`}>{title}</h3>
      <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground" data-testid={`${testId}-body`}>{body}</p>
      {action && <div className="mt-5" data-testid={`${testId}-action`}>{action}</div>}
    </section>
  );
}

export function Money({ amount, currency, testId }) {
  return <span data-testid={testId}>{formatMoney(amount, currency)}</span>;
}